import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Navigation, Tag } from 'lucide-react';
import { Img } from '@/components/ui';
import { formatINR } from '@/lib/catalog';
import { FillingFastBadge } from '@/components/event/FillingFast';

/**
 * Poster card used across the home grid, venue pages and "similar events".
 * Everything on it is derived from the normalized event model.
 */
const EventCard = ({ event, index = 0, compact = false }) => {
  if (!event) return null;

  // A teaser has no ticket tiers yet, so a "₹0" or "Free entry" price would be
  // a lie. Announce the state instead of a number.
  const priceLabel = event.comingSoon
    ? 'Tickets open soon'
    : event.isFree
      ? 'Free entry'
      : `₹${formatINR(event.priceFrom)}${event.priceTo > event.priceFrom ? ' onwards' : ''}`;

  return (
    // CSS stagger, not a JS one. This card is the entire catalogue; if the
    // animation ever fails to run, a JS `initial={{opacity:0}}` leaves the
    // grid blank rather than merely unanimated.
    <div
      className="group h-full animate-floatUp"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <Link to={`/event/${event.id}`} className="lk-focus block h-full rounded-2xl">
        <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-cardHover">
          <div className="relative">
            <Img
              src={event.thumbnail}
              alt={event.title}
              ratio="3 / 4"
              imgClassName="transition-transform duration-500 group-hover:scale-105"
            />

            {/* Top-left status badges */}
            <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
              {event.comingSoon ? (
                <span className="rounded-full bg-teaser px-2.5 py-1 text-[11px] font-semibold text-white">
                  Coming soon
                </span>
              ) : event.soldOut ? (
                <span className="rounded-full bg-ink-900/90 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                  Sold out
                </span>
              ) : event.fillingFast || event.lowStock ? (
                <FillingFastBadge left={event.fillingFastLeft} />
              ) : null}
              {event.distanceKm != null && (
                <span className="inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
                  <Navigation className="h-3 w-3" />
                  {event.distanceKm} km
                </span>
              )}
            </div>

            {/* Offer ribbon, mirroring the admin-configured sale price */}
            {event.discountPct > 0 && !event.comingSoon && (
              <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-brand-secondary px-3 py-2 text-[12px] font-semibold text-white">
                <Tag className="h-3.5 w-3.5" />
                Flat {event.discountPct}% OFF
              </div>
            )}
          </div>

          <div className={`flex flex-1 flex-col ${compact ? 'p-3' : 'p-4'}`}>
            <p className="truncate text-xs font-semibold uppercase tracking-wide text-brand-secondary">
              {event.dateLabel}
              {event.timeLabel ? `, ${event.timeLabel}` : ''}
            </p>

            <h3 className="mt-1.5 line-clamp-2 text-[15px] font-semibold leading-snug text-ink-900">
              {event.title}
            </h3>

            <p className="mt-1 flex items-start gap-1 text-xs text-ink-500">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-1">
                {[event.venueName, event.location?.city].filter(Boolean).join(', ')}
              </span>
            </p>

            <p
              className={`mt-auto pt-3 text-sm font-semibold ${
                event.comingSoon ? 'text-teaser' : 'text-ink-900'
              }`}
            >
              {priceLabel}
            </p>
          </div>
        </article>
      </Link>
    </div>
  );
};

export default EventCard;
